// @flow
import { Helmet } from 'react-helmet';
import get from 'lodash/get';
import mergeWith from 'lodash/mergeWith';
import getLinkTags from 'utils/getLinkTags';
import { ROOT_LOCALE } from 'utils/constants';
import type { PageContextType } from './PageContext';
import { imageTransform } from './Image';
import * as componentKeys from '../componentKeys';

type PropsType = {
  pageContext: PageContextType
};

const ogLocales = {
  en: 'en_JP',
  ja: 'ja_JP',
  ko: 'ko_KR',
  fr: 'fr_FR'
};

export default function SeoContent({ pageContext }: PropsType) {
  const pageType = get(pageContext, 'story.field_component');
  const heroContent = get(pageContext, 'story.content.hero_content');
  const previewImage = get(pageContext, 'story.content.preview_image');
  const contentHeroImage = get(pageContext, 'story.content.hero_image');
  const shouldHidePageFromSearchEngines = get(
    pageContext,
    'story.content.hide_from_search_engines'
  );
  let lang = ROOT_LOCALE;
  if (pageContext.country && pageContext.country.code) {
    lang = pageContext.country.code;
  }
  const htmlAttributes = {
    lang
  };
  const linkTags = getLinkTags(pageContext.links);

  const mergedSeoDefinitions = mergeWith(
    {},
    get(pageContext, 'layout.default_seo', {}),
    get(pageContext, 'story.content.seo', {}),
    (a, b) => b || a
  );

  const { description, title } = mergedSeoDefinitions;
  let {
    og_description: ogDescription,
    og_title: ogTitle,
    twitter_description: twitterDescription,
    twitter_title: twitterTitle,
    og_image: ogImage,
    twitter_image: twitterImage
  } = mergedSeoDefinitions;

  if (!ogTitle) {
    ogTitle = title;
  }
  if (!ogDescription) {
    ogDescription = description;
  }
  if (!twitterTitle) {
    twitterTitle = title;
  }
  if (!twitterDescription) {
    twitterDescription = description;
  }
  let ogType = 'website';
  let twitterType = 'product';
  if (
    pageType === componentKeys.BLOG_AGGREGATE_PAGE ||
    pageType === componentKeys.BLOG_POST
  ) {
    ogType = 'blog';
    twitterType = 'summary';
  }
  if (!ogImage || !twitterImage) {
    let heroImage;
    if (heroContent && heroContent.length) {
      // Normal Page
      heroImage = heroContent[0].image;
    } else if (previewImage) {
      // Use Blog Page Preview by preference
      heroImage = previewImage;
    } else if (contentHeroImage) {
      // If for some reason Blog Preview isn't set, use the blog hero image
      heroImage = contentHeroImage;
    }
    ogImage = ogImage || heroImage;
    twitterImage = twitterImage || heroImage;
  }
  const ogUrl = pageContext.links
    ? `https://www.tablecheck.com${pageContext.links.canonical}`
    : '';
  const ogLocale = pageContext.country
    ? ogLocales[pageContext.country.code]
    : ogLocales[ROOT_LOCALE];
  if (!title) {
    throw new Error(
      `Page ${get(
        pageContext,
        'story.full_slug',
        'unknown page'
      )} is missing a SEO Title and the country/language default has not been set`
    );
  }
  return (
    <Helmet htmlAttributes={htmlAttributes}>
      <title>{title}</title>
      {description && <meta name="description" content={description} />}
      {ogTitle && <meta property="og:title" content={ogTitle} />}
      {ogDescription && (
        <meta property="og:description" content={ogDescription} />
      )}
      {ogImage && (
        <meta
          property="og:image"
          content={imageTransform(ogImage, {
            size: { width: 1200, height: 630 }
          })}
        />
      )}
      {twitterTitle && <meta name="twitter:title" content={twitterTitle} />}
      {twitterDescription && (
        <meta name="twitter:description" content={twitterDescription} />
      )}
      {twitterImage && (
        <meta
          name="twitter:image"
          content={imageTransform(twitterImage, {
            size: { width: 1080, height: 540 }
          })}
        />
      )}
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:type" content={ogType} />
      {ogUrl && <meta property="og:url" content={ogUrl} />}
      <meta name="twitter:card" content={twitterType} />
      <meta name="twitter:site" content="@tablecheck" />
      {shouldHidePageFromSearchEngines && (
        <meta name="robots" content="noindex" />
      )}
      {linkTags}
    </Helmet>
  );
}
